<template>
  <v-app>
    <v-main>
        <div class="page">
            <div class="bg-texture"></div>
            <div class="glow"></div>

            <v-card class="login-card" flat>
                <div class="d-flex justify-center mb-2">
                    <v-img
                        src="@/assets/logo.png"
                        alt="School Logo"
                        width="120"
                        contain
                        class="flex-grow-0"
                    />
                </div>

                <div class="eyebrow">Preysal Secondary School</div>
                <h1 class="title">Staff Portal</h1>

                <v-alert :type="alert.type" v-if="alert.show">
                    {{ alert.message }}
                </v-alert>

                <v-form @submit.prevent="login" ref="form">
                    <v-text-field
                        v-model="formData.name"
                        label="Username"
                        clearable
                        v-on:keyup="keyPress"
                        auto-complete="off"
                        placeholder="Enter your username"
                        outlined
                        dense
                        autofocus
                        class="mt-8"
                    />

                    <v-text-field
                        v-model="formData.password"
                        :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                        :type="show ? 'text' : 'password'"
                        label="Password"
                        @click:append="show = !show" 
                        placeholder="Enter your password"
                        outlined
                        dense
                        clearable
                        ref="password" 
                        v-on:keyup="keyPressPassword"
                    />

                    <v-btn
                        class="login-btn"
                        @click="submit"
                        block
                        large
                    >
                        Sign In
                    </v-btn>
                </v-form>

                <a href="https://preysalsecondary.ttschoolnet.com" class="back-link">← Back to Portal</a>
            </v-card>
        </div>

        <v-overlay
            :value="overlay"
            opacity="0.8"
            z-index="15"                    
        >
            
            <v-progress-circular
                indeterminate
                size="64"
            ></v-progress-circular>                   
        </v-overlay>
    </v-main>
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

export default {
  name: "Login",

  created(){
    if(this.passwordWasReset){
        this.alert.message = 'Your password has been reset, please sign in with new password.'
        this.alert.type = 'success'
        this.alert.show = true
    } 
  },

  computed: {
    ...mapGetters({
        passwordWasReset: 'auth/getPasswordWasReset',
    })  
  },

  data() {
    return {
        formData: {
            name: '',
            password: '',            
        },
        show: false, 
        overlay: false,
        alert:{
            show: false,
            message: '',
            type: 'error'
        }
    };
  },

  methods: {
    ...mapActions({
        signIn: 'auth/signIn',
        getUserPermissions: 'auth/userPermissions',
        authenticated: 'auth/getAuthenticated',
    }),

    ...mapMutations({
        setCredentials: 'auth/setCredentials',
        setUser: 'auth/setUser',
        setAuthenticated: 'auth/setAuthenticated',
        setEmployeeSignedIn: 'auth/setEmployeeSignedIn',
        setEmployeeInitials: 'auth/setEmployeeInitials',
        setAdmin: 'auth/setAdmin', 
        setPermissions: 'auth/setPermissions',
        setEmployeeId: 'auth/setEmployeeId',
        setResetPassword: 'auth/setResetPassword',
        setPasswordWasReset: 'auth/setPasswordWasReset',
    }),

    async submit() {
        if(!this.formData.name || !this.formData.password){
            this.alert.type = 'error';
            this.alert.message = 'Please enter your username and password';
            this.alert.show = true;
            return;
        }

        this.setCredentials(this.formData);
        this.overlay = true;

        try {
            const { data } = await this.signIn();
            this.setAuthenticated(true);
            sessionStorage.setItem('authenticated', true);

            sessionStorage.setItem('employeeId', data.employee_id ? data.employee_id : data.name);
            
            const user = {
                employee_id: data.employee_id ? data.employee_id : data.name
            }

            this.setUser(JSON.stringify(user));
            sessionStorage.setItem('user', JSON.stringify(user));

            this.alert.message = '';
            this.alert.show = false;

            const { name, password_reset, employee_id, first_name, last_name } = data;

            if(name == 'Admin'){
                sessionStorage.setItem('employeeSignedIn', 'Admin');
                sessionStorage.setItem('employeeInitials', 'A')  
                sessionStorage.setItem('admin', 1);
                this.setAdmin(true);              
                this.setEmployeeSignedIn('Admin');
                this.setEmployeeInitials('A');

                this.$router.replace('/app');
            }
            
            if(name !== 'Admin' && password_reset == 1){
                this.setResetPassword(data.password_reset);
                sessionStorage.setItem('resetPassword', data.password_reset);
                this.$router.replace('/reset-password');
            }

            if(name !== 'Admin' && password_reset == 0){
                this.setPasswordWasReset(false);
                sessionStorage.setItem('passwordWasReset', false);
                console.log(data);

                this.setEmployeeId(employee_id);
                sessionStorage.setItem('employeeSignedIn', first_name + ' ' + last_name);
                sessionStorage.setItem('employeeInitials', first_name[0] + last_name[0]);
                this.setEmployeeSignedIn(first_name + ' ' + last_name);
                this.setEmployeeInitials(first_name[0] + last_name[0]);

                const { data: dataPermissions } = await this.getUserPermissions();
                sessionStorage.setItem('permissions', JSON.stringify(dataPermissions));

                this.$router.replace('/app');
            }
            
        } catch (error) {
            console.log(error.response || error);

            let errorMessage =
                error.response?.data?.message ||
                Object.values(error.response?.data?.errors || {})[0]?.[0] ||
                error.message ||
                "Unknown error";

            this.alert.message = `An error has occurred: ${errorMessage}`;
            this.alert.type = 'error';
            this.alert.show = true;
        } finally {
            this.overlay = false;
        }
    },

    keyPress (e) {
        if(e.keyCode === 13){
            this.$refs.password.focus();
        }
    },

    keyPressPassword (e) {
        if(e.keyCode === 13){
            this.submit();
        }
    }
  }
};
</script>

<style scoped>
.page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
  background: #faf6ef;
}

/* background pattern */
.bg-texture {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(27,33,66,0.05) 1px, transparent 1px);
  background-size: 28px 28px;
  opacity: 0.6;
}

/* glow effect */
.glow {
  position: absolute;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(201,152,47,0.18), transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* card */
.login-card {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 420px;
  padding: 40px;
  border-radius: 24px;
  border: 1px solid rgba(27,33,66,0.06);
}

/* logo */
.logo-wrap {
  text-align: center;
  margin-bottom: 24px;
}

/* small heading */
.eyebrow {
  text-align: center;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.25em;
  color: #716c62;
  margin-bottom: 8px;
}

/* title */
.title {
  text-align: center;
  margin: 0 0 32px;
  font-family: 'Fraunces', serif;
  font-size: 34px;
  color: #1B2142;
}


/* button */
.login-btn {
  background: linear-gradient(135deg, var(--v-primary-base), var(--v-primary-lighten1));
  color: white;
  font-weight: 600;
  border-radius: 14px;
}

/* back link */
.back-link {
  display: block;
  text-align: center;
  margin-top: 20px;
  color: #2C3568;
  text-decoration: none;
  font-size: 14px;
}
</style>